import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import Icon from "@/components/Icon";

export default function Solutions() {
  const { content, loading } = useContent();
  if (loading || !content) return null;
  const s = content.solutions || {};

  return (
    <div data-testid="page-solutions">
      <section className="sp-hero">
        <div className="sp-container py-16 lg:py-20">
          {s.eyebrow && <span className="sp-eyebrow">{s.eyebrow}</span>}
          <h1 className="mt-3 text-4xl lg:text-6xl font-extrabold leading-tight">
            <span className="text-gold">{s.title}</span>
            <br />
            {s.title_accent}
          </h1>
          {s.description && (
            <p className="mt-4 max-w-3xl text-slate-600 text-lg">{s.description}</p>
          )}
        </div>
      </section>

      {/* Outcomes */}
      {s.outcomes?.length > 0 && (
        <section className="sp-strip">
          <div className="sp-container py-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 sp-chip" style={{ background: "rgba(245,180,23,.18)", color: "#f5b417" }}>
                <Icon name="Target" size={14} /> We Solve Real Problems. We Deliver Measurable Results.
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
              {s.outcomes.map((o, i) => (
                <div key={o.label + i} className="flex flex-col items-center text-center" data-testid={`outcome-${i}`}>
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-gold">
                    <Icon name={o.icon} size={26} />
                  </div>
                  <div className="mt-3 text-sm font-bold uppercase tracking-wide text-white leading-tight">
                    {o.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Solutions grid */}
      <section className="py-20">
        <div className="sp-container">
          <div className="text-center mb-12">
            <span className="sp-eyebrow">Organized By Customer Problem</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold">
              Pick the outcome you need — <span className="text-gold">we'll build the path.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(s.items || []).map((item, i) => (
              <div key={item.title + i} className="sp-card h-full flex flex-col" data-testid={`solution-${item.n || i + 1}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-full text-white flex items-center justify-center font-extrabold"
                    style={{ background: item.color }}
                  >
                    {item.n || i + 1}
                  </div>
                  <h3 className="text-[1.05rem] font-extrabold leading-tight" style={{ color: item.color }}>
                    {item.title}
                  </h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <div
                      className="inline-block text-[0.7rem] font-bold uppercase tracking-wider px-2 py-1 rounded-md"
                      style={{ background: `${item.color}15`, color: item.color }}
                    >
                      Problem
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{item.problem}</p>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Solution</div>
                    <p className="mt-1 text-[0.95rem] font-bold" style={{ color: item.color }}>
                      {item.solution}
                    </p>
                  </div>
                </div>
                <ul className="mt-4 space-y-1.5 border-t border-slate-100 pt-4">
                  {(item.bullets || []).map((b, j) => (
                    <li key={b + j} className="flex items-center gap-2 text-sm text-slate-700">
                      <Icon name="CheckCircle2" size={16} style={{ color: item.color }} /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars + CTA */}
      <section className="sp-strip">
        <div className="sp-container py-14 grid md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="sp-chip" style={{ background: "rgba(245,180,23,.18)", color: "#f5b417" }}>
              <Icon name="Target" size={14} /> Outcomes
            </div>
            <h3 className="mt-3 text-2xl lg:text-3xl font-extrabold text-white">
              We don't just build software.
              <br />
              <span className="text-gold">We deliver outcomes that drive impact.</span>
            </h3>
          </div>
          <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {(s.pillars || []).map((p, i) => (
              <div key={p.label + i} className="bg-white/10 rounded-2xl p-4 text-center" data-testid={`pillar-${i}`}>
                <div className="w-11 h-11 mx-auto rounded-xl bg-white/10 flex items-center justify-center text-gold">
                  <Icon name={p.icon} size={22} />
                </div>
                <div className="mt-2 text-xs font-bold uppercase tracking-wide text-white leading-tight">
                  {p.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sp-container pb-14 flex justify-center">
          <Link to="/contact" className="sp-btn-gold" data-testid="solutions-cta">
            <Icon name="Calendar" size={18} /> Schedule a Consultation <Icon name="ArrowRight" size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
